import { useEffect } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import type { Metric, Period, Scope } from "@/api/types";

export interface Filters {
  period: Period;
  metric: Metric;
  scope: Scope;
}

export const DEFAULT_FILTERS: Filters = {
  period: "month_current",
  metric: "messages",
  scope: "all",
};

/** Normalize a raw period value read from the URL. Accepts the legacy
 * `month` value and maps it to `month_current` for URL back-compat. */
function normalizePeriod(raw: unknown): Period | undefined {
  if (raw === "month") return "month_current";
  return raw as Period | undefined;
}

/** Read/write period + metric + scope from the URL search params.
 * Keeping state in the URL makes it shareable and avoids a redundant
 * client-side store. */
export function useFilters(): {
  filters: Filters;
  setFilter: <K extends keyof Filters>(key: K, value: Filters[K]) => void;
} {
  const search = useSearch({ strict: false }) as Partial<Filters> & {
    period?: Period | "month";
  };
  const navigate = useNavigate();

  const normalizedPeriod = normalizePeriod(search.period);

  // Deep-link back-compat: rewrite `?period=month` to `?period=month_current`
  // on first read so downstream consumers (and the URL the user sees) are
  // consistent with the new value set.
  useEffect(() => {
    if (search.period === "month") {
      void navigate({
        to: ".",
        search: (prev) => ({
          ...(prev as Record<string, unknown>),
          period: "month_current",
        }),
        replace: true,
      });
    }
  }, [search.period, navigate]);

  const filters: Filters = {
    period: normalizedPeriod ?? DEFAULT_FILTERS.period,
    metric: search.metric ?? DEFAULT_FILTERS.metric,
    scope: search.scope ?? DEFAULT_FILTERS.scope,
  };

  const setFilter = <K extends keyof Filters>(key: K, value: Filters[K]) => {
    void navigate({
      to: ".",
      search: (prev) => ({
        ...(prev as Record<string, unknown>),
        [key]: value,
      }),
      replace: true,
    });
  };

  return { filters, setFilter };
}
